import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { Almanac } from "protos/robot_syncer/plant_profile";
import { AlmanacConfig, Formula } from "protos/almanac/almanac";
import { classes } from "portal/theme/theme";
import {
  comparisonAlmanacForOriginCategoryId,
  ResolvedOriginCategory,
  translateCategoryName,
} from "portal/utils/categoryCollection/category";
import {
  FORMULA_FIELD_KEYS,
  getFormulasFromBands,
  getSharedAdvancedFormula,
  getShootTimeBands,
  isValidAlmanac,
  syncAdvancedFieldsToFirst,
} from "portal/utils/shootTime";
import { isUndefined } from "common/utils/identity";
import { ShootTimeChart } from "./chart/ShootTimeChart";
import { ShootTimeInfoPopover } from "./ShootTimeInfoPopover";
import { ShootTimeValues } from "./values/ShootTimeValues";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { WhenTrue } from "common/utils/types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from "react";
import SwapHorizIcon from "@mui/icons-material/SwapHorizOutlined";

type ShootTimeSectionProps = {
  almanac?: Almanac;
  carbonDefaultAlmanac?: AlmanacConfig;
  originCategory?: ResolvedOriginCategory;
  subtitle?: string;
  paperClassName?: string;
} & WhenTrue<
  "compact",
  {
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
  }
> &
  WhenTrue<
    "canEdit",
    {
      onChange: (formulas: Formula[]) => void;
      onSizesChange: (sizes: number[]) => void;
      onReset: () => void;
      isResettable: boolean;
      onFieldValidityChange: (fieldId: string, isValid: boolean) => void;
    }
  >;

export const ShootTimeSection: FunctionComponent<ShootTimeSectionProps> = ({
  almanac,
  carbonDefaultAlmanac,
  originCategory,
  subtitle: subnote,
  paperClassName,
  canEdit,
  compact = false,
  expanded,
  onExpandedChange,
  onChange,
  onSizesChange,
  onReset,
  isResettable,
  onFieldValidityChange,
}) => {
  const { t } = useTranslation();
  const comparisonLabel = comparisonLabelForOriginCategory(t, originCategory);
  const comparisonAlmanac = useMemo(
    () =>
      comparisonAlmanacForOriginCategoryId(
        carbonDefaultAlmanac,
        almanac?.originCategoryId
      ),
    [almanac?.originCategoryId, carbonDefaultAlmanac]
  );
  const {
    palette: {
      chart: { bandStrokeColors },
    },
  } = useTheme();

  const bands = useMemo(() => {
    if (!isValidAlmanac(almanac)) {
      return [];
    }
    return getShootTimeBands(
      t,
      almanac.sizeBoundariesMm,
      almanac.formulas,
      bandStrokeColors
    );
  }, [almanac, bandStrokeColors, t]);

  const areAdvancedFormulasSeparate = useMemo(
    () =>
      isUndefined(getSharedAdvancedFormula(bands.map((band) => band.formula))),
    [bands]
  );
  const [preferTuneAdvancedSeparate, setPreferTuneAdvancedSeparate] =
    useState(false); // we normally derive state based on almanac, but let user tune separately even if formulas temporarily align
  const tuneSeparately = canEdit
    ? areAdvancedFormulasSeparate || preferTuneAdvancedSeparate
    : areAdvancedFormulasSeparate;
  const sharedAdvancedFormula = tuneSeparately ? undefined : bands[0]?.formula;

  const clearBandAdvancedValidity = useCallback(() => {
    if (!canEdit) {
      return;
    }
    for (let bandIndex = 0; bandIndex < bands.length; bandIndex++) {
      for (const field of FORMULA_FIELD_KEYS) {
        onFieldValidityChange(`band-${bandIndex}-${field}`, true);
      }
    }
  }, [bands.length, canEdit, onFieldValidityChange]);

  const onTuneSeparatelyChange = useCallback(
    (checked: boolean) => {
      if (!canEdit) {
        return;
      }
      setPreferTuneAdvancedSeparate(checked);
      if (checked) {
        return;
      }
      clearBandAdvancedValidity();
      if (areAdvancedFormulasSeparate) {
        onChange(syncAdvancedFieldsToFirst(getFormulasFromBands(bands)));
      }
    },
    [
      areAdvancedFormulasSeparate,
      bands,
      canEdit,
      clearBandAdvancedValidity,
      onChange,
    ]
  );

  const hasContent = bands.length > 0;

  const body = canEdit ? (
    <ShootTimeSectionBody
      bands={bands}
      comparisonAlmanac={comparisonAlmanac}
      comparisonLabel={comparisonLabel}
      sharedAdvancedFormula={sharedAdvancedFormula}
      hasContent={hasContent}
      tuneSeparately={tuneSeparately}
      onTuneSeparatelyChange={onTuneSeparatelyChange}
      canEdit
      onChange={onChange}
      onSizesChange={onSizesChange}
      onFieldValidityChange={onFieldValidityChange}
    />
  ) : (
    <ShootTimeSectionBody
      bands={bands}
      comparisonAlmanac={comparisonAlmanac}
      comparisonLabel={comparisonLabel}
      sharedAdvancedFormula={sharedAdvancedFormula}
      hasContent={hasContent}
      tuneSeparately={tuneSeparately}
      onTuneSeparatelyChange={onTuneSeparatelyChange}
    />
  );

  if (compact) {
    return (
      <Paper elevation={0} className={classes("rounded-sm", paperClassName)}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded}
          onChange={(_, open) => {
            onExpandedChange?.(open);
          }}
          className="before:hidden bg-transparent"
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="min-h-0 px-3"
            classes={{ content: "my-2 w-full mr-2 items-center" }}
          >
            <ShootTimeSectionHeader
              compact
              canEdit={canEdit}
              isResettable={isResettable}
              onReset={onReset}
            />
          </AccordionSummary>
          <AccordionDetails className="flex flex-col gap-3 px-3 pb-3 pt-0">
            {subnote && (
              <Typography variant="caption" color="text.secondary">
                {subnote}
              </Typography>
            )}
            {body}
          </AccordionDetails>
        </Accordion>
      </Paper>
    );
  }

  return (
    <Card
      className="flex flex-col gap-3 mt-6 p-4 rounded-sm"
      variant="outlined"
    >
      <div className="flex flex-col gap-1">
        <ShootTimeSectionHeader
          canEdit={canEdit}
          isResettable={isResettable}
          onReset={onReset}
        />
        {subnote && (
          <Typography variant="body2" color="text.secondary">
            {subnote}
          </Typography>
        )}
      </div>
      {body}
    </Card>
  );
};

interface ShootTimeSectionHeaderProps {
  compact?: boolean;
  canEdit?: boolean;
  isResettable?: boolean;
  onReset?: () => void;
}

const ShootTimeSectionHeader: FunctionComponent<
  ShootTimeSectionHeaderProps
> = ({ compact = false, canEdit = false, isResettable = false, onReset }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-2 w-full min-w-0">
      <Typography
        variant={compact ? "body2" : "h5"}
        component="h2"
        className={classes("flex items-center gap-1.5", {
          uppercase: compact,
        })}
      >
        {compact
          ? t("views.categoryTemplates.tabs.shootTime")
          : t("views.categoryTemplates.shootTime.title")}
        <ShootTimeInfoPopover />
      </Typography>
      {canEdit && (
        // stays mounted while clean so becoming dirty does not resize the row
        <Button
          size="small"
          variant="contained"
          className={isResettable ? undefined : "invisible"}
          aria-hidden={!isResettable}
          disabled={!isResettable}
          onClick={(event) => {
            event.stopPropagation(); // compact renders this inside the accordion toggle
            onReset?.();
          }}
        >
          {t("utils.actions.resetLong", {
            subject: t("views.categoryTemplates.tabs.shootTime"),
          })}
        </Button>
      )}
    </div>
  );
};

type ShootTimeSectionBodyProps = {
  bands: ReturnType<typeof getShootTimeBands>;
  comparisonAlmanac?: Almanac;
  comparisonLabel?: string;
  sharedAdvancedFormula?: Formula;
  hasContent: boolean;
  tuneSeparately: boolean;
  onTuneSeparatelyChange: (checked: boolean) => void;
} & WhenTrue<
  "canEdit",
  {
    onChange: (formulas: Formula[]) => void;
    onSizesChange: (sizes: number[]) => void;
    onFieldValidityChange: (fieldId: string, isValid: boolean) => void;
  }
>;

const ShootTimeSectionBody: FunctionComponent<ShootTimeSectionBodyProps> = ({
  bands,
  comparisonAlmanac,
  comparisonLabel,
  sharedAdvancedFormula,
  hasContent,
  tuneSeparately,
  onTuneSeparatelyChange,
  canEdit,
  onChange,
  onSizesChange,
  onFieldValidityChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {canEdit && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Typography
            variant="body2"
            color="text.secondary"
            className="flex items-center gap-1.5"
          >
            <SwapHorizIcon fontSize="small" aria-hidden />
            {t("views.categoryTemplates.shootTime.dragDividersHint")}
          </Typography>
          <FormControlLabel
            className="self-end"
            control={
              <Checkbox
                color="info"
                checked={tuneSeparately}
                onChange={(event) => {
                  onTuneSeparatelyChange(event.currentTarget.checked);
                }}
              />
            }
            label={t(
              "views.categoryTemplates.shootTime.tuneEachSizeSeparately"
            )}
          />
        </div>
      )}
      {hasContent ? (
        <div className="flex flex-col gap-6 w-full">
          <div className="w-full min-w-0">
            {canEdit ? (
              <ShootTimeChart
                bands={bands}
                className="md:min-h-[300px]"
                comparisonAlmanac={comparisonAlmanac}
                comparisonLabel={comparisonLabel}
                formulaVersion="2.5"
                canEdit
                onSizesChange={onSizesChange}
                onFieldValidityChange={onFieldValidityChange}
              />
            ) : (
              <ShootTimeChart
                bands={bands}
                className="md:min-h-[300px]"
                comparisonAlmanac={comparisonAlmanac}
                comparisonLabel={comparisonLabel}
                formulaVersion="2.5"
              />
            )}
          </div>
          {canEdit ? (
            <ShootTimeValues
              bands={bands}
              sharedAdvancedFormula={sharedAdvancedFormula}
              canEdit
              onChange={onChange}
              onFieldValidityChange={onFieldValidityChange}
            />
          ) : (
            <ShootTimeValues
              bands={bands}
              sharedAdvancedFormula={sharedAdvancedFormula}
            />
          )}
        </div>
      ) : (
        <Paper
          variant="section"
          className="flex items-center justify-center min-h-16 italic"
        >
          <Typography>{t("utils.descriptors.none")}</Typography>
        </Paper>
      )}
    </>
  );
};

const comparisonLabelForOriginCategory = (
  t: TFunction,
  originCategory: ResolvedOriginCategory | undefined
): string | undefined => {
  if (!originCategory) {
    return undefined;
  }
  if (originCategory === "unknown") {
    return t("utils.descriptors.unknown");
  }
  return t("views.categoryTemplates.shootTime.comparisonLabel", {
    category: translateCategoryName(t, originCategory),
  });
};
